import { queryOptions, useQuery } from '@tanstack/react-query';
import type {
  CdpOutIntegration,
  DataOutIntegration,
  IntegrationsGetSegmentOutLegacyResponse,
  MParticleIntegration,
} from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { integrationKeys } from './entity';

/**
 * Single-object integration status reads (mParticle, CDP out, Segment in/out,
 * data-out). The "not configured" case is a fetch error that resolves to
 * `null`/`false` rather than surfacing, so the directory shows the integration
 * as simply not-enabled.
 */

const STALE_TIME = 60_000;

const statusKey = (workspaceId: number, name: string) => [...integrationKeys.all(workspaceId), 'status', name] as const;

/* ------------------------------- mParticle ------------------------------- */

/** @public The mParticle integration config, or `null` when not configured. */
export async function getMparticleIntegration(workspaceId: number): Promise<MParticleIntegration | null> {
  try {
    const data = await hydraUiApi.integrations.getMparticle({
      params: { path: { environment_id: String(workspaceId) } },
    });
    return data.mparticle ?? null;
  } catch {
    return null;
  }
}

export const getMparticleIntegrationQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: statusKey(workspaceId, 'mparticle'),
    queryFn: () => getMparticleIntegration(workspaceId),
    staleTime: STALE_TIME,
  });

/** @public */
export const useMparticleIntegration = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getMparticleIntegrationQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getMparticleIntegrationQueryOptions(workspaceId), ...queryConfig });
};

/* --------------------------------- CDP out ------------------------------- */

/** @public Whether a CDP-out integration is configured (drives CDP in + out). */
export async function getCdpOutEnabled(workspaceId: number): Promise<boolean> {
  try {
    const data = await hydraUiApi.integrations.getCdpOut({
      params: { path: { environment_id: String(workspaceId) } },
    });
    const cdpOut: CdpOutIntegration | undefined = data.cdp_out;
    return cdpOut != null;
  } catch {
    return false;
  }
}

export const getCdpOutEnabledQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: statusKey(workspaceId, 'cdp_out'),
    queryFn: () => getCdpOutEnabled(workspaceId),
    staleTime: STALE_TIME,
  });

/** @public */
export const useCdpOutEnabled = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getCdpOutEnabledQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getCdpOutEnabledQueryOptions(workspaceId), ...queryConfig });
};

/* ------------------------------- Segment in ------------------------------ */

/** @public Whether the Segment (Destination) integration is present. */
export async function getSegmentInPresent(workspaceId: number): Promise<boolean> {
  try {
    await hydraUiApi.integrations.getSegmentIn({
      params: { path: { environment_id: String(workspaceId) } },
    });
    return true;
  } catch {
    return false;
  }
}

export const getSegmentInPresentQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: statusKey(workspaceId, 'segment_in'),
    queryFn: () => getSegmentInPresent(workspaceId),
    staleTime: STALE_TIME,
  });

/** @public */
export const useSegmentInPresent = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getSegmentInPresentQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getSegmentInPresentQueryOptions(workspaceId), ...queryConfig });
};

/* ------------------------- Segment out (segdotcom) ----------------------- */

/**
 * @public The full Segment-out (segdotcom) status, or `null` when not configured.
 *
 * Returned as the API sends it. Unlike its `segment_out` sibling this response is
 * not enveloped — the fields sit at the top level.
 */
export async function getSegmentOut(workspaceId: number): Promise<IntegrationsGetSegmentOutLegacyResponse | null> {
  try {
    return await hydraUiApi.integrations.getSegdotcom({
      params: { path: { environment_id: String(workspaceId) } },
    });
  } catch {
    return null;
  }
}

export const getSegmentOutQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: statusKey(workspaceId, 'segdotcom'),
    queryFn: () => getSegmentOut(workspaceId),
    staleTime: STALE_TIME,
  });

/**
 * @public Whether the Segment (Source) integration is enabled — selected off the
 * full read so the directory gate and the connections overview share one fetch.
 */
export const getSegmentOutEnabledQueryOptions = (workspaceId: number) =>
  queryOptions({
    ...getSegmentOutQueryOptions(workspaceId),
    select: (data: IntegrationsGetSegmentOutLegacyResponse | null) => data?.enabled ?? false,
  });

/** @public */
export const useSegmentOutEnabled = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getSegmentOutEnabledQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getSegmentOutEnabledQueryOptions(workspaceId), ...queryConfig });
};

/* -------------------------------- Data out ------------------------------- */

/**
 * @public The full NativeDataOut (amplitude / mixpanel) config for one identifier,
 * or `null` when not configured (a 404). Returned as the API sends it — unlike
 * `getDataOutEnabled` (a presence check) this keeps the timestamps + events the
 * connection card needs.
 */
export async function getDataOut(workspaceId: number, identifier: string): Promise<DataOutIntegration | null> {
  try {
    const data = await hydraUiApi.integrations.getDataOut({
      params: { path: { environment_id: String(workspaceId), identifier } },
    });
    return data.data_out ?? null;
  } catch {
    return null;
  }
}

export const getDataOutQueryOptions = (workspaceId: number, identifier: string) =>
  queryOptions({
    queryKey: statusKey(workspaceId, `data_out:${identifier}`),
    queryFn: () => getDataOut(workspaceId, identifier),
    staleTime: STALE_TIME,
  });

/**
 * @public Whether a NativeDataOut integration (amplitude / mixpanel) is enabled —
 * selected off the full read so the directory gate and the connections overview
 * share one fetch per identifier.
 */
export const getDataOutEnabledQueryOptions = (workspaceId: number, identifier: string) =>
  queryOptions({
    ...getDataOutQueryOptions(workspaceId, identifier),
    select: (data: DataOutIntegration | null) => data != null,
  });
